import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { authAPI } from '../api/authAPI';

const DeleteAccountPage: React.FC = () => {
  const [password, setPassword] = useState('');
  const [confirmText, setConfirmText] = useState('');
  const [showFinalConfirm, setShowFinalConfirm] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  console.log('DeleteAccountPage 렌더링');

  // 1단계: 경고 및 비밀번호 확인
  const handleFirstStep = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    
    // 이메일 사용자는 비밀번호 필요
    if (user?.social_provider === 'email' && (!password || password.trim() === '')) {
      setError(t('delAcct.errPassword'));
      return;
    }
    
    setShowFinalConfirm(true);
  };

  // 2단계: 최종 확인
  const handleFinalDelete = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    try {
      if (confirmText !== t('delAcct.confirmPhrase')) {
        setError(t('delAcct.errPhrase'));
        setLoading(false);
        return;
      }
      
      const response = await authAPI.deleteAccount({
        password: user?.social_provider === 'email' ? password : undefined,
        confirmText
      });
      
      console.log('회원탈퇴 성공:', response);
      
      // 로그아웃 처리
      logout();
      
      // 탈퇴 완료 페이지로 이동
      navigate('/', { 
        replace: true,
        state: { 
          message: t('delAcct.doneMsg'),
          type: 'info'
        }
      });
      
    } catch (err: any) {
      console.error('회원탈퇴 에러:', err);
      setError(err.response?.data?.message || t('delAcct.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setShowFinalConfirm(false);
    setPassword('');
    setConfirmText('');
    setError('');
  };

  const goBack = () => {
    navigate('/profile');
  };

  if (showFinalConfirm) {
    // 2단계: 최종 확인 모달
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000
      }}>
        <div style={{
          backgroundColor: '#fff',
          padding: '40px',
          borderRadius: '12px',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
          maxWidth: '500px',
          width: '90%',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '20px' }}>⚠️</div>
          <h2 style={{ color: '#dc3545', marginBottom: '20px', fontSize: '24px' }}>
            ⚠️ {t('delAcct.finalConfirm')} ⚠️
          </h2>
          <div style={{
            backgroundColor: '#f8f9fa',
            padding: '20px',
            borderRadius: '8px',
            marginBottom: '25px',
            border: '2px solid #dc3545'
          }}>
            <p style={{ color: '#333', fontSize: '16px', margin: '10px 0' }}>
              <strong>{t('delAcct.effectsTitle')}</strong>
            </p>
            <ul style={{ textAlign: 'left', color: '#666', fontSize: '14px', lineHeight: '1.6' }}>
              <li>{t('delAcct.li1')}</li>
              <li>{t('delAcct.li2')}</li>
              <li>{t('delAcct.li3', { name: user?.nickname })}</li>
              <li>{t('delAcct.li4')}</li>
            </ul>
          </div>
          
          <form onSubmit={handleFinalDelete}>
            <div style={{ marginBottom: '20px' }}>
              <p style={{ marginBottom: '10px', fontWeight: '600', color: '#333' }}>
                {t('delAcct.typePhrase')}
              </p>
              <p style={{ 
                backgroundColor: '#e9ecef', 
                padding: '8px 12px', 
                borderRadius: '4px',
                fontWeight: 'bold',
                color: '#495057',
                marginBottom: '10px'
              }}>
                {t('delAcct.confirmPhrase')}
              </p>
              <input
                type="text"
                placeholder={t('delAcct.phrasePlaceholder')}
                value={confirmText}
                onChange={e => setConfirmText(e.target.value)}
                required
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '2px solid #dc3545',
                  borderRadius: '8px',
                  fontSize: '16px',
                  boxSizing: 'border-box',
                  textAlign: 'center'
                }}
              />
            </div>
            
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
              <button
                type="button"
                onClick={handleCancel}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '16px',
                  fontWeight: '600'
                }}
              >
                {t('common.cancel')}
              </button>
              <button
                type="submit"
                disabled={loading || confirmText !== t('delAcct.confirmPhrase')}
                style={{
                  padding: '12px 24px',
                  backgroundColor: loading || confirmText !== t('delAcct.confirmPhrase') ? '#ccc' : '#dc3545',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: loading || confirmText !== t('delAcct.confirmPhrase') ? 'not-allowed' : 'pointer',
                  fontSize: '16px',
                  fontWeight: '600'
                }}
              >
                {loading ? t('tradeModal.processing') : t('delAcct.title')}
              </button>
            </div>
            
            {error && (
              <div style={{
                color: '#dc3545',
                marginTop: '15px',
                padding: '12px',
                backgroundColor: '#f8d7da',
                border: '1px solid #f5c6cb',
                borderRadius: '8px',
                fontSize: '14px'
              }}>
                {error}
              </div>
            )}
          </form>
        </div>
      </div>
    );
  }

  // 1단계: 경고 및 비밀번호 확인
  return (
    <div style={{
      maxWidth: 600,
      margin: '40px auto',
      padding: '40px',
      backgroundColor: '#fff',
      borderRadius: '12px',
      boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
    }}>
      <div style={{ textAlign: 'center', marginBottom: '30px' }}>
        <div style={{ fontSize: '48px', marginBottom: '10px' }}>⚠️</div>
        <h1 style={{ color: '#dc3545', fontSize: '28px', marginBottom: '10px' }}>
          {t('delAcct.title')}
        </h1>
        <p style={{ color: '#666', fontSize: '16px' }}>
          {t('delAcct.subtitle')}
        </p>
      </div>

      <div style={{
        backgroundColor: '#fff5f5',
        border: '2px solid #fed7d7',
        borderRadius: '8px',
        padding: '25px',
        marginBottom: '30px'
      }}>
        <h3 style={{ color: '#c53030', marginBottom: '15px', fontSize: '18px' }}>
          ⚠️ {t('delAcct.warnTitle')}
        </h3>
        <ul style={{ color: '#333', fontSize: '15px', lineHeight: '1.8', paddingLeft: '20px' }}>
          <li>{t('delAcct.w1')}</li>
          <li>{t('delAcct.w2')}</li>
          <li>{t('delAcct.w3', { name: user?.nickname })}</li>
          <li>{t('delAcct.w4')}</li>
          <li>{t('delAcct.w5')}</li>
        </ul>
      </div>

      <form onSubmit={handleFirstStep}>
        {user?.social_provider === 'email' && (
          <div style={{ marginBottom: '25px' }}>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              fontWeight: '600',
              color: '#333'
            }}>
              {t('delAcct.pwLabel')}
            </label>
            <input
              type="password"
              placeholder={t('delAcct.pwPlaceholder')}
              value={password}
              onChange={e => setPassword(e.target.value)}
              required
              style={{
                width: '100%',
                padding: '14px',
                border: '2px solid #e8e8e8',
                borderRadius: '8px',
                fontSize: '16px',
                boxSizing: 'border-box'
              }}
            />
          </div>
        )}

        {user?.social_provider !== 'email' && (
          <div style={{
            backgroundColor: '#e3f2fd',
            padding: '15px',
            borderRadius: '8px',
            marginBottom: '25px',
            border: '1px solid #bbdefb'
          }}>
            <p style={{ color: '#1565c0', fontSize: '14px', margin: 0 }}>
              💡 {t('delAcct.socialNote', { provider: user?.social_provider })}
            </p>
          </div>
        )}

        <div style={{ display: 'flex', gap: '15px', justifyContent: 'center' }}>
          <button
            type="button"
            onClick={goBack}
            style={{
              padding: '14px 28px',
              backgroundColor: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '16px',
              fontWeight: '600'
            }}
          >
            {t('common.cancel')}
          </button>
          <button
            type="submit"
            style={{
              padding: '14px 28px',
              backgroundColor: '#dc3545',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '16px',
              fontWeight: '600',
              transition: 'all 0.2s'
            }}
            onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#c82333'}
            onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#dc3545'}
          >
            {t('delAcct.continueBtn')}
          </button>
        </div>

        {error && (
          <div style={{
            color: '#dc3545',
            marginTop: '20px',
            padding: '12px',
            backgroundColor: '#f8d7da',
            border: '1px solid #f5c6cb',
            borderRadius: '8px',
            textAlign: 'center',
            fontSize: '14px'
          }}>
            {error}
          </div>
        )}
      </form>
    </div>
  );
};

export default DeleteAccountPage;
